import { useEffect } from "react";

// Vertical-then-horizontal scroll — using the standard web pattern.
//   • The Opener (IntroPanel) and About (AboutPanel) are ordinary 100vh
//     sections stacked in normal document flow.
//   • Below About lives an `.hscroll-zone` sized as `range + 100vh`. Inside
//     it a sticky container pins for the full range and its child `.hall` is
//     translated horizontally in step with the native scrollY.
//   • On small screens (≤900px) the hall falls back to a plain vertical stack
//     (`.is-vertical`) — native, smooth, no scroll-jacking on touch devices.
const VERTICAL_QUERY = "(max-width: 900px)";
const NAV_OFFSET = 72;

export default function useHorizontalScroll() {
  useEffect(() => {
    const hall = document.querySelector(".hall");
    const zone = document.querySelector(".hscroll-zone");
    const root = document.querySelector(".gallery-root");
    if (!hall || !zone) return;

    const mq = window.matchMedia(VERTICAL_QUERY);
    const isVertical = () => mq.matches;

    const maxX = () => Math.max(0, hall.scrollWidth - window.innerWidth);
    const zoneTop = () => zone.offsetTop;

    const measure = () => {
      root?.classList.toggle("is-vertical", isVertical());
      if (isVertical()) {
        zone.style.height = "";
        hall.style.transform = "";
        return;
      }
      zone.style.height = maxX() + window.innerHeight + "px";
    };

    const nav = () => document.querySelector(".topnav");

    const apply = () => {
      const y = window.scrollY;
      const vertical = isVertical();

      let progress = 0;
      if (vertical) {
        const total = document.documentElement.scrollHeight - window.innerHeight;
        progress = total > 0 ? y / total : 0;
      } else {
        const range = maxX();
        const x = Math.max(0, Math.min(range, y - zoneTop()));
        hall.style.transform = `translate3d(${-x}px, 0, 0)`;
        progress = range ? x / range : 0;
      }

      const fade = Math.min(1, Math.max(0, y / (window.innerHeight * 0.6)));
      const navEl = nav();
      if (navEl) {
        navEl.style.opacity = String(fade);
        navEl.style.pointerEvents = fade < 0.05 ? "none" : "auto";
      }
      document.documentElement.style.setProperty("--scroll-progress", progress);
    };

    measure();
    apply();

    const onScroll = () => apply();
    const onResize = () => { measure(); apply(); };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    mq.addEventListener?.("change", onResize);

    const ro = new ResizeObserver(measure);
    ro.observe(hall);
    window.addEventListener("load", onResize);
    const settleTimers = [500, 1500, 3000].map((t) => setTimeout(onResize, t));

    const smooth = (top, immediate) =>
      window.scrollTo({ top, behavior: immediate ? "auto" : "smooth" });

    window.__hscroll = {
      // Scroll so that `el` (anywhere on the page) is in view — in horizontal
      // mode hall children map to a scrollY along the walk.
      toElement: (el, immediate = false) => {
        if (!el) return;
        if (!isVertical() && hall.contains(el)) {
          const x = el.getBoundingClientRect().left - hall.getBoundingClientRect().left;
          smooth(zoneTop() + Math.min(Math.max(0, x), maxX()), immediate);
          return;
        }
        const top = el.getBoundingClientRect().top + window.scrollY - (hall.contains(el) ? NAV_OFFSET : 0);
        smooth(Math.max(0, top), immediate);
      },
      scrollTo: (x, immediate = false) => {
        if (isVertical()) return;
        smooth(zoneTop() + Math.min(Math.max(0, x), maxX()), immediate);
      },
      toIntro: (immediate = false) => smooth(0, immediate),
      toAbout: (immediate = false) => {
        const about = document.querySelector(".panel--about");
        if (about) smooth(about.offsetTop, immediate);
      },
      get vertical() {
        return isVertical();
      },
      get max() {
        return maxX();
      },
    };

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("load", onResize);
      mq.removeEventListener?.("change", onResize);
      ro.disconnect();
      settleTimers.forEach(clearTimeout);
      delete window.__hscroll;
    };
  }, []);
}
